import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import productAnalyticsService from '../../services/api/product-analytics.service';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

export default function DevicesPage() {
  const { t } = useTranslation('analytics');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 86400000, endTime: Date.now(), rangeType: 'preset', presetValue: '7d',
  });
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState<Array<{ deviceType: string; sessions: number; uniqueUsers: number }>>([]);
  const [browserData, setBrowserData] = useState<Array<{ browser: string; sessions: number; uniqueUsers: number }>>([]);
  const [osData, setOsData] = useState<Array<{ os: string; sessions: number; uniqueUsers: number }>>([]);
  const [screenData, setScreenData] = useState<Array<{ resolution: string; sessions: number; uniqueUsers: number }>>([]);

  const appId = selectedAppId || 'all';
  const startISO = new Date(timeRange.startTime).toISOString();
  const endISO = new Date(timeRange.endTime).toISOString();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [devices, browsers, os, screens] = await Promise.all([
        productAnalyticsService.getDeviceBreakdown(appId, startISO, endISO),
        productAnalyticsService.getBrowserBreakdown(appId, startISO, endISO),
        productAnalyticsService.getOSBreakdown(appId, startISO, endISO),
        productAnalyticsService.getScreenBreakdown(appId, startISO, endISO),
      ]);
      setDeviceData(devices || []);
      setBrowserData(browsers || []);
      setOsData(os || []);
      setScreenData(screens || []);
    } catch (err) {
      console.error('Failed to load device data:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, startISO, endISO]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalSessions = deviceData.reduce((s, d) => s + d.sessions, 0);
  const mobilePercent = totalSessions > 0 ? ((deviceData.find(d => d.deviceType?.toLowerCase() === 'mobile')?.sessions || 0) / totalSessions * 100).toFixed(1) : '0';
  const topBrowser = browserData[0]?.browser || '-';

  const fmt = (n: number) => { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); };
  const pct = (n: number) => totalSessions > 0 ? ((n / totalSessions) * 100).toFixed(1) : '0.0';

  const devicePieData = deviceData.map(d => ({ name: d.deviceType || 'Unknown', value: d.sessions }));

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('devices.title')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('devices.subtitle')}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* 3 Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('devices.totalSessions')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(totalSessions)}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('devices.mobilePercent')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{mobilePercent}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('devices.popularBrowser')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white truncate">{topBrowser}</p>
          </div>
        </div>

        {/* Device Type PieChart + Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('devices.deviceDistribution')}</h2>
            {devicePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={devicePieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" paddingAngle={3}>
                    {devicePieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-12 text-gray-500">{t('devices.noData')}</div>}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('devices.deviceType')}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 font-medium">{t('devices.deviceType')}</th>
                  <th className="pb-2 font-medium text-right">{t('devices.sessions')}</th>
                  <th className="pb-2 font-medium text-right">{t('devices.uniqueUsers')}</th>
                  <th className="pb-2 font-medium text-right">{t('devices.percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {deviceData.map(d => (
                  <tr key={d.deviceType} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                    <td className="py-2.5 font-medium text-gray-900 dark:text-white">{d.deviceType || t('devices.unknown')}</td>
                    <td className="py-2.5 text-right">{fmt(d.sessions)}</td>
                    <td className="py-2.5 text-right text-gray-500">{fmt(d.uniqueUsers)}</td>
                    <td className="py-2.5 text-right text-gray-500">{pct(d.sessions)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Browser BarChart + Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('devices.browserDistribution')}</h2>
            {browserData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={browserData.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="browser" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="sessions" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-12 text-gray-500">{t('devices.noData')}</div>}
          </div>

          {/* OS BarChart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('devices.osDistribution')}</h2>
            {osData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={osData.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="os" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="sessions" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-12 text-gray-500">{t('devices.noData')}</div>}
          </div>
        </div>

        {/* Screen Resolutions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('devices.screenResolutions')}</h2>
          {screenData.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 font-medium">{t('devices.resolution')}</th>
                  <th className="pb-2 font-medium text-right">{t('devices.sessions')}</th>
                  <th className="pb-2 font-medium text-right">{t('devices.percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {screenData.map(s => (
                  <tr key={s.resolution} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                    <td className="py-2.5 font-mono text-gray-900 dark:text-white">{s.resolution}</td>
                    <td className="py-2.5 text-right">{fmt(s.sessions)}</td>
                    <td className="py-2.5 text-right text-gray-500">{pct(s.sessions)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-center py-8 text-gray-500">{t('devices.noData')}</div>}
        </div>
      </div>
    </MainLayout>
  );
}
